import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { focusRing } from '../lib/focus-ring';
import { Button } from './Button';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHeader,
  TableHeaderCell,
  TableRow,
  type TableAlign,
} from './Table';

/* -------------------------------------------------------------------------- */
/* types                                                                       */
/* -------------------------------------------------------------------------- */

export interface DataTableColumn<TRow> {
  /** Stable identity for the column; also the default sort key. */
  key: string;
  header: ReactNode;
  /** Renders the cell body. Keep it presentational — no data fetching. */
  cell: (row: TRow, index: number) => ReactNode;
  align?: TableAlign;
  /** Show a sort control in the header. Requires `onSortChange`. */
  sortable?: boolean;
  /** Sort key reported to `onSortChange`; defaults to `key`. */
  sortKey?: string;
  /** Any CSS length, applied to both the header cell and its column. */
  width?: string;
  headerClassName?: string;
  cellClassName?: string;
}

export type SortDirection = 'asc' | 'desc';

export interface DataTableSort {
  key: string;
  direction: SortDirection;
}

export interface DataTablePagination {
  /** 1-based. */
  page: number;
  pageSize: number;
  /** Total matching rows, when the server knows it. */
  total?: number;
  /** Used instead of `total` when the count is unknown. */
  hasNext?: boolean;
  onPageChange: (page: number) => void;
}

export interface DataTableProps<TRow> {
  columns: ReadonlyArray<DataTableColumn<TRow>>;
  rows: readonly TRow[];
  /** Stable key per row — an id, not the array index, wherever one exists. */
  getRowKey: (row: TRow, index: number) => string;
  /**
   * Accessible name for the table and its scroll region. Required: a table
   * without a name is unnavigable by screen reader, and the sr-only `<caption>`
   * this renders costs nothing visually.
   */
  label: string;
  loading?: boolean;
  /** Skeleton rows shown while loading with nothing to display yet. */
  loadingRows?: number;
  /** Replaces the default message when there are no rows and no loading. */
  empty?: ReactNode;
  /** Sorting is controlled — the caller re-queries and passes new rows back. */
  sort?: DataTableSort | null;
  onSortChange?: (sort: DataTableSort) => void;
  pagination?: DataTablePagination;
  /** Width below which the table stops shrinking and the container scrolls. */
  minWidth?: string;
  /** Extra classes on a body row, e.g. to mark a rejected row. */
  rowClassName?: (row: TRow, index: number) => string | undefined;
  className?: string;
}

/* -------------------------------------------------------------------------- */
/* header sort control                                                         */
/* -------------------------------------------------------------------------- */

function SortButton({
  children,
  direction,
  onClick,
}: {
  children: ReactNode;
  direction: SortDirection | null;
  onClick: () => void;
}) {
  const Icon = direction === null ? ChevronsUpDown : direction === 'asc' ? ChevronUp : ChevronDown;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        '-mx-1 inline-flex cursor-pointer items-center gap-1.5 rounded-xs px-1 py-0.5',
        'font-mono text-[0.7rem] font-medium uppercase leading-none tracking-[0.11em]',
        'text-muted transition-colors duration-150 hover:text-text',
        direction !== null && 'text-text',
        focusRing,
      )}
    >
      {children}
      <Icon
        aria-hidden="true"
        className={cn('size-3 shrink-0', direction === null && 'opacity-45')}
      />
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* DataTable                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The table Wend shows records in: columns config, controlled sorting,
 * pagination controls, a loading state and an empty state.
 *
 *     <DataTable
 *       label="Contacts"
 *       columns={columns}
 *       rows={page.items}
 *       getRowKey={(contact) => contact.id}
 *       loading={isLoading}
 *       sort={sort}
 *       onSortChange={setSort}
 *       pagination={{ page, pageSize: 25, total: page.total, onPageChange: setPage }}
 *     />
 *
 * A wide table scrolls inside its own container; the page body never does.
 */
export function DataTable<TRow>({
  columns,
  rows,
  getRowKey,
  label,
  loading = false,
  loadingRows = 5,
  empty,
  sort = null,
  onSortChange,
  pagination,
  minWidth,
  rowClassName,
  className,
}: DataTableProps<TRow>) {
  const columnCount = columns.length;
  const showSkeleton = loading && rows.length === 0;
  const showEmpty = !loading && rows.length === 0;

  /** Current direction for a column, or null when it is not the sorted one. */
  const directionFor = (columnKey: string): SortDirection | null =>
    sort !== null && sort.key === columnKey ? sort.direction : null;

  const handleSort = (column: DataTableColumn<TRow>) => {
    if (onSortChange === undefined) return;
    const key = column.sortKey ?? column.key;
    onSortChange({ key, direction: directionFor(key) === 'asc' ? 'desc' : 'asc' });
  };

  return (
    <div
      className={cn(
        'flex min-w-0 flex-col overflow-hidden rounded-lg border border-line bg-white/[0.02]',
        className,
      )}
    >
      {/* Permanent live region: mounting one that already has content is
          unreliably announced, so it exists from the start and only its text
          changes. */}
      <div role="status" aria-live="polite" className="sr-only">
        {loading ? 'Loading' : ''}
      </div>

      <TableContainer label={label} bordered={false}>
        <Table minWidth={minWidth} aria-busy={loading || undefined}>
          <caption className="sr-only">{label}</caption>

          <TableHeader>
            <TableRow>
              {columns.map((column) => {
                const key = column.sortKey ?? column.key;
                const sortable = column.sortable === true && onSortChange !== undefined;
                const direction = directionFor(key);

                return (
                  <TableHeaderCell
                    key={column.key}
                    align={column.align}
                    className={column.headerClassName}
                    style={column.width === undefined ? undefined : { width: column.width }}
                    aria-sort={
                      sortable
                        ? direction === null
                          ? 'none'
                          : sortDirectionToAria(direction)
                        : undefined
                    }
                  >
                    {sortable ? (
                      <SortButton direction={direction} onClick={() => handleSort(column)}>
                        {column.header}
                      </SortButton>
                    ) : (
                      column.header
                    )}
                  </TableHeaderCell>
                );
              })}
            </TableRow>
          </TableHeader>

          <TableBody className={cn(loading && rows.length > 0 && 'opacity-55')}>
            {showSkeleton &&
              Array.from({ length: Math.max(1, loadingRows) }, (_, rowIndex) => (
                <TableRow key={`wend-skeleton-${rowIndex}`} aria-hidden="true">
                  {columns.map((column) => (
                    <TableCell key={column.key}>
                      <span className="block h-3.5 max-w-[14rem] animate-pulse rounded-xs bg-white/[0.08]" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {showEmpty && (
              <TableRow>
                <TableCell colSpan={columnCount} className="px-4 py-12 text-center">
                  {empty ?? <p className="text-[0.9rem] text-muted">No rows to show.</p>}
                </TableCell>
              </TableRow>
            )}

            {!showSkeleton &&
              rows.map((row, index) => (
                <TableRow key={getRowKey(row, index)} className={rowClassName?.(row, index)}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.align}
                      className={column.cellClassName}
                    >
                      {column.cell(row, index)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination !== undefined && (
        <Pagination pagination={pagination} rowCount={rows.length} loading={loading} />
      )}
    </div>
  );
}

function sortDirectionToAria(direction: SortDirection): 'ascending' | 'descending' {
  return direction === 'asc' ? 'ascending' : 'descending';
}

/* -------------------------------------------------------------------------- */
/* pagination                                                                  */
/* -------------------------------------------------------------------------- */

function Pagination({
  pagination,
  rowCount,
  loading,
}: {
  pagination: DataTablePagination;
  rowCount: number;
  loading: boolean;
}) {
  const { page, pageSize, total, hasNext, onPageChange } = pagination;

  const from = rowCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = rowCount === 0 ? 0 : from + rowCount - 1;

  const canPrevious = page > 1 && !loading;
  const canNext =
    !loading && (total === undefined ? (hasNext ?? rowCount === pageSize) : page * pageSize < total);

  let range: string;
  if (total !== undefined && total === 0) range = 'No rows';
  else if (rowCount === 0) range = `Page ${page}`;
  else if (total === undefined) range = `${from}–${to} · page ${page}`;
  else range = `${from}–${to} of ${total}`;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3">
      <p className="font-mono text-[0.7rem] uppercase tracking-[0.11em] text-muted">{range}</p>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={!canPrevious}
          onClick={() => onPageChange(page - 1)}
          leadingIcon={<ChevronLeft aria-hidden="true" />}
        >
          Previous
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={!canNext}
          onClick={() => onPageChange(page + 1)}
          trailingIcon={<ChevronRight aria-hidden="true" />}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
